// src/components/ProductCard.jsx - Enhanced with User-Friendly Options Modal
import React, { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Eye, Tag, Package, Star, Settings, X, Check, AlertCircle } from 'lucide-react';
import { cartAPI, productsAPI } from '../services/api';
import { track } from '../utils/metaPixel';
import './css/ProductCard.css';

const optionStateCache = new Map();
const productDetailsCache = new Map();

const getOptionState = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return { hasRequiredOptions: false, hasOnlyOptionalOptions: false, hasAnyOptions: false };
  }
  const hasRequiredOptions = options.some(group => !!group.is_required);
  const hasAnyOptions = options.length > 0;
  const hasOnlyOptionalOptions = hasAnyOptions && !hasRequiredOptions;
  return { hasRequiredOptions, hasOnlyOptionalOptions, hasAnyOptions };
};

const ProductCard = ({ product, openCart }) => {
  const navigate = useNavigate();
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [hasRequiredOptions, setHasRequiredOptions] = useState(false);
  const [hasOnlyOptionalOptions, setHasOnlyOptionalOptions] = useState(false);
  const [hasAnyOptions, setHasAnyOptions] = useState(false);

  useEffect(() => {
    if (!showOptionsModal) return;
    document.body.classList.add('productcard-modal-open');
    return () => {
      document.body.classList.remove('productcard-modal-open');
    };
  }, [showOptionsModal]);

  // Initial computations for card display (based on partial product from search)
  const isOutOfStock = product.stock <= 0;
  const productPath = product.slug ? `/product/${product.slug}` : `/product/${product.id}`;

  // Fetch options info if not present (for button text accuracy)
  useEffect(() => {
    let isMounted = true;
    const fetchOptionsInfo = async () => {
      const cachedState = optionStateCache.get(product.id);
      if (cachedState) {
        setHasRequiredOptions(cachedState.hasRequiredOptions);
        setHasOnlyOptionalOptions(cachedState.hasOnlyOptionalOptions);
        setHasAnyOptions(cachedState.hasAnyOptions);
        return;
      }

      if (product.options && product.options.length > 0) {
        const state = getOptionState(product.options);
        optionStateCache.set(product.id, state);
        productDetailsCache.set(product.id, product);
        setHasRequiredOptions(state.hasRequiredOptions);
        setHasOnlyOptionalOptions(state.hasOnlyOptionalOptions);
        setHasAnyOptions(state.hasAnyOptions);
        return;
      }

      // Fetch if not present
      try {
        setButtonLoading(true);
        const cachedProduct = productDetailsCache.get(product.id);
        if (cachedProduct) {
          const state = getOptionState(cachedProduct.options);
          optionStateCache.set(product.id, state);
          setHasRequiredOptions(state.hasRequiredOptions);
          setHasOnlyOptionalOptions(state.hasOnlyOptionalOptions);
          setHasAnyOptions(state.hasAnyOptions);
          return;
        }
        const response = await productsAPI.getById(product.id);
        if (!isMounted) return;
        const fullProduct = response.data;
        productDetailsCache.set(product.id, fullProduct);
        const state = getOptionState(fullProduct.options);
        optionStateCache.set(product.id, state);
        setHasRequiredOptions(state.hasRequiredOptions);
        setHasOnlyOptionalOptions(state.hasOnlyOptionalOptions);
        setHasAnyOptions(state.hasAnyOptions);
      } catch (err) {
        console.error('Failed to fetch options info:', err);
      } finally {
        if (isMounted) {
          setButtonLoading(false);
        }
      }
    };

    fetchOptionsInfo();
    return () => {
      isMounted = false;
    };
  }, [product.id, product.options]);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    setLoading(true);
    let targetProduct = product;
    let fetchFailed = false;

    // If options not present (common in search results), fetch full product
    if (!product.options || product.options.length === 0) {
      try {
        const cachedProduct = productDetailsCache.get(product.id);
        if (cachedProduct) {
          targetProduct = { ...product, ...cachedProduct };
        } else {
          const response = await productsAPI.getById(product.id);
          productDetailsCache.set(product.id, response.data);
          targetProduct = { ...product, ...response.data };
        }
      } catch (err) {
        console.error('Failed to fetch product details:', err);
        fetchFailed = true;
      }
    }

    setLoading(false);

    if (fetchFailed) {
      alert('Impossible de charger les options du produit. Consultez les details pour personnaliser.');
      return;
    }

    // Now check with targetProduct
    if (targetProduct.options && targetProduct.options.length > 0) {
      setModalProduct(targetProduct);
      setShowOptionsModal(true);
      return;
    }

    // No options: add directly
    await performAddToCart({ productId: targetProduct.id, quantity: 1, selected_options: {} }, targetProduct);
  };

  const handleOptionSelect = (groupId, valueId) => {
    setSelectedOptions(prev => ({ ...prev, [groupId]: valueId }));
  };

  // Use modalProduct when modal is open, fallback to product
  const currentOptions = modalProduct?.options || product.options;

  // FIXED: Use !! for is_required check, with currentOptions
  const isOptionsComplete = () => {
    if (!currentOptions || currentOptions.length === 0) return true;
    const requiredGroups = currentOptions.filter(g => !!g.is_required);
    if (requiredGroups.length === 0) return true; // All optional: complete
    return requiredGroups.every(g => !!selectedOptions[g.id]);
  };

  // FIXED: Guard against undefined currentOptions to prevent filter error
  const getMissingRequiredGroups = () => {
    if (!currentOptions) return [];
    const requiredGroups = currentOptions.filter(g => !!g.is_required);
    return requiredGroups.filter(g => !selectedOptions[g.id]);
  };

  const hasRequiredOptionsModal = currentOptions && currentOptions.some(g => !!g.is_required);
  const hasOnlyOptionalOptionsModal = currentOptions && currentOptions.length > 0 && !hasRequiredOptionsModal;

  const handleConfirmOptions = async () => {
    if (!isOptionsComplete()) {
      return; // Visual feedback already shown
    }
    await performAddToCart({ 
      productId: modalProduct.id, 
      quantity: 1, 
      selected_options: selectedOptions 
    }, modalProduct);
    setShowOptionsModal(false);
    setModalProduct(null);
    setSelectedOptions({});
  };

  const handleSkipOptions = async () => {
    // Only allow skip if there are no required options
    if (hasRequiredOptionsModal) return;
    await performAddToCart({ 
      productId: modalProduct.id, 
      quantity: 1, 
      selected_options: {} 
    }, modalProduct);
    setShowOptionsModal(false);
    setModalProduct(null);
    setSelectedOptions({});
  };

  const performAddToCart = async (itemData, targetProduct) => {
    try {
      setLoading(true);
      await cartAPI.addItem(itemData);
      const resolvedProduct = targetProduct || product;
      if (resolvedProduct?.id) {
        const itemValue = getItemValue(resolvedProduct, itemData.selected_options);
        track('AddToCart', {
          content_ids: [String(resolvedProduct.id)],
          content_name: resolvedProduct.name,
          content_type: 'product',
          value: itemValue,
          currency: 'TND'
        });
      }
      openCart();
    } catch (err) {
      alert(err.response?.data?.message || "Impossible d'ajouter au panier");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = () => {
    navigate(productPath);
  };

  const getPrice = (price) => Number(price || 0).toFixed(2);

  const getOptionExtras = (targetProduct, selections) => {
    if (!targetProduct?.options || !selections) return 0;
    return targetProduct.options.reduce((sum, group) => {
      const selectedValueId = selections[group.id];
      if (!selectedValueId) return sum;
      const selectedValue = group.values?.find(v => v.id === Number(selectedValueId));
      const extra = Number(selectedValue?.extra_price || 0);
      return sum + extra;
    }, 0);
  };

  const getItemValue = (targetProduct, selections) => {
    const basePrice = Number(targetProduct?.salePrice ?? targetProduct?.regularPrice ?? 0);
    return basePrice + getOptionExtras(targetProduct, selections);
  };

  const discountPercentage = product.salePrice && product.regularPrice
    ? Math.round(((product.regularPrice - product.salePrice) / product.regularPrice) * 100)
    : 0;

  const averageRating = product.averageRating != null ? Number(product.averageRating) : null;
  const ratingDisplay = averageRating !== null && !isNaN(averageRating)
    ? averageRating.toFixed(1)
    : null;

  // Calculate total price with selected options (use modalProduct when available)
  const calculateTotalPrice = () => {
    const target = modalProduct || product;
    const basePrice = target.salePrice || target.regularPrice;
    let extraPrice = 0;
    
    target.options?.forEach(group => {
      const selectedValueId = selectedOptions[group.id];
      if (selectedValueId) {
        const selectedValue = group.values.find(v => v.id === selectedValueId);
        if (selectedValue && selectedValue.extra_price) {
          extraPrice += Number(selectedValue.extra_price);
        }
      }
    });
    
    return (Number(basePrice) + extraPrice).toFixed(2);
  };

  const missingRequired = getMissingRequiredGroups();
  const showWarning = hasRequiredOptionsModal && missingRequired.length > 0;

  // Button text logic using fetched states
  const buttonText = isOutOfStock 
    ? 'Rupture de stock' 
    : (hasRequiredOptions 
        ? 'Choisir les options' 
        : (hasOnlyOptionalOptions ? 'Ajouter (personnalisable)' : 'Ajouter au panier')
      );

  const isButtonDisabled = isOutOfStock || loading || buttonLoading;

  return (
    <>
      <div
        className="productcard-container"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
      >
        <div className="productcard-image-wrapper">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="productcard-image"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="productcard-image-placeholder" />
          )}
          {(hasAnyOptions || (product.options && product.options.length > 0)) && (
            <div className="productcard-options-badge">
              <Settings size={12} />
              {hasRequiredOptions ? 'Options requises' : 'Personnalisable'}
            </div>
          )}
          {discountPercentage > 0 && (
            <div className="productcard-badge">-{discountPercentage}%</div>
          )}
          {isOutOfStock && (
            <div className="productcard-stock-overlay">Rupture de stock</div>
          )}
        </div>
        
        <div className="productcard-content">
          <div className="productcard-category">
            <Tag size={14} />
            <span>{product.categoryName}</span>
          </div>
          
          <h3 className="productcard-title">{product.name}</h3>
          
          <div className="productcard-price-section">
            {product.salePrice ? (
              <>
                <span className="productcard-sale-price">DT {getPrice(product.salePrice)}</span>
                <span className="productcard-regular-price">DT {getPrice(product.regularPrice)}</span>
              </>
            ) : (
              <span className="productcard-sale-price">DT {getPrice(product.regularPrice)}</span>
            )}
            {ratingDisplay && (
              <div className="productcard-rating">
                <Star size={14} className="filled" fill="#FFD700" color="#FFD700" />
                <span>{ratingDisplay}</span>
              </div>
            )}
          </div>

          <div className="productcard-stock-info">
            <Package size={14} />
            <span>{product.stock} en stock</span>
            {(hasAnyOptions || (product.options && product.options.length > 0)) && (
              <span className="productcard-options-info">
                + {hasRequiredOptions ? 'options requises' : 'options optionnelles'}
              </span>
            )}
          </div>
          
          <div className="productcard-actions">
            <button
              onClick={handleAddToCart}
              disabled={isButtonDisabled}
              className={`productcard-button-cart ${isOutOfStock ? 'disabled' : ''}`}
            >
              <ShoppingCart size={18} />
              {buttonLoading ? 'Chargement...' : buttonText}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(productPath);
              }}
              className="productcard-button-view"
              aria-label="Voir les details du produit"
              disabled={buttonLoading}
            >
              <Eye size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Options Modal */}
      {showOptionsModal && modalProduct && typeof document !== 'undefined' && createPortal(
        <div className="productcard-modal-overlay" onClick={() => setShowOptionsModal(false)}>
          <div className="productcard-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="productcard-modal-header">
              <div className="productcard-modal-header-content">
                <h3 className="productcard-modal-title">
                  Personnalisez votre commande
                </h3>
                <p className="productcard-modal-subtitle">{modalProduct.name}</p>
              </div>
              <button 
                onClick={() => setShowOptionsModal(false)} 
                className="productcard-modal-close"
                aria-label="Fermer la modale"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="productcard-modal-body">
              {/* Info Banner */}
              {hasOnlyOptionalOptionsModal && (
                <div className="productcard-modal-banner productcard-modal-banner-info">
                  <AlertCircle size={20} />
                  <span>Toutes les options sont facultatives. Vous pouvez ignorer la personnalisation et ajouter au panier.</span>
                </div>
              )}

              {/* Options Groups */}
              <div className="productcard-options-list">
                {modalProduct.options.map((group, index) => {
                  const isRequired = !!group.is_required;
                  const isSelected = !!selectedOptions[group.id];
                  const isMissing = isRequired && !isSelected;

                  return (
                    <div 
                      key={group.id} 
                      className={`productcard-option-group ${isMissing ? 'productcard-option-group-error' : ''} ${isSelected ? 'productcard-option-group-selected' : ''}`}
                    >
                      {/* Group Header */}
                      <div className="productcard-option-header">
                        <div className="productcard-option-header-left">
                          <span className="productcard-option-number">{index + 1}</span>
                          <h4 className="productcard-option-title">{group.name}</h4>
                        </div>
                        {isRequired ? (
                          <span className="productcard-option-required-badge">Requis</span>
                        ) : (
                          <span className="productcard-option-optional-badge">Optionnel</span>
                        )}
                      </div>

                      {/* Option Values */}
                      <div className="productcard-option-values">
                        {group.values.map(value => {
                          const isValueSelected = selectedOptions[group.id] === value.id;
                          
                          return (
                            <label 
                              key={value.id} 
                              className={`productcard-option-value ${isValueSelected ? 'productcard-option-value-selected' : ''}`}
                            >
                              <input
                                type="radio"
                                name={`group-${group.id}`}
                                value={value.id}
                                checked={isValueSelected}
                                onChange={() => handleOptionSelect(group.id, value.id)}
                              />
                              <span className="productcard-option-checkmark">
                                {isValueSelected && <Check size={16} />}
                              </span>
                              <span className="productcard-option-text-content">
                                <span className="productcard-option-value-name">{value.value}</span>
                                {value.extra_price > 0 && (
                                  <span className="productcard-option-extra-price">+DT {Number(value.extra_price).toFixed(2)}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Warning Banner */}
              {showWarning && (
                <div className="productcard-modal-banner productcard-modal-banner-warning">
                  <AlertCircle size={20} />
                  <div>
                    <strong>Selections requises manquantes</strong>
                    <p>Veuillez selectionner : {missingRequired.map(g => g.name).join(', ')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="productcard-modal-footer">
              <div className="productcard-modal-footer-info">
                <span className="productcard-modal-total-label">Prix total :</span>
                <span className="productcard-modal-total-price">DT {calculateTotalPrice()}</span>
              </div>
              <div className="productcard-modal-footer-actions">
                {hasOnlyOptionalOptionsModal && (
                  <button
                    onClick={handleSkipOptions}
                    disabled={loading}
                    className="productcard-modal-button productcard-modal-button-secondary"
                  >
                    Ignorer et ajouter au panier
                  </button>
                )}
                <button
                  onClick={handleConfirmOptions}
                  disabled={loading || !isOptionsComplete()}
                  className="productcard-modal-button productcard-modal-button-primary"
                >
                  {loading ? (
                    <>
                      <span className="productcard-modal-loading"></span>
                      Ajout...
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={18} />
                      Ajouter au panier
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default memo(ProductCard);
